import { useEffect, useState } from "react";
import { LogOut, Menu, Plus, BellRing } from "lucide-react";
import { Logo } from "@/components/Layout";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { GROUPS } from "@/admin/nav";
import { AdminSearch } from "@/admin/AdminSearch";

const NEW = [["staff", "Add pickup partner"], ["team", "Add team login"], ["rewards", "Add reward"], ["materials", "Edit material rates"], ["news", "Fetch latest news"]];

export const SidebarBody = ({ admin, alerts, cur, go, onLogout }) => (
  <div className="flex h-full flex-col bg-[#0D3B2B] text-white">
    <div className="px-6 pb-4 pt-6"><Logo light className="h-8" /><p className="mt-2 text-[10px] font-bold uppercase tracking-[.25em] text-white/50">Operations Console</p></div>
    <nav className="flex-1 space-y-5 overflow-y-auto px-3 pb-6" data-testid="admin-sidebar">
      {GROUPS.map(([g, items]) => {
        const vis = items.filter((s) => s.roles.includes(admin.role));
        if (!vis.length) return null;
        return (
          <div key={g}>
            <p className="px-3 pb-1.5 text-[10px] font-bold uppercase tracking-[.18em] text-white/45">{g}</p>
            {vis.map((s) => {
              const n = s.badge && alerts?.[s.badge];
              const on = cur.key === s.key;
              return (
                <button key={s.key} onClick={() => go(s.key)} data-testid={`admin-tab-${s.key}`}
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-semibold transition-colors duration-150 ${on ? "bg-[#F3EDDC] text-[#0D3B2B]" : "text-white/85 hover:bg-white/10"}`}>
                  <s.icon className="h-[18px] w-[18px] shrink-0" /><span className="flex-1">{s.label}</span>
                  {!!n && <span data-testid={`admin-badge-${s.key}`} className="rounded-full bg-[#E05C3A] px-2 py-0.5 text-[10px] font-bold text-white">{n}</span>}
                </button>
              );
            })}
          </div>
        );
      })}
    </nav>
    <div className="border-t border-white/10 p-4">
      <p className="truncate text-xs text-white/70">{admin.email}</p>
      <div className="mt-2 flex items-center justify-between">
        <span className="rounded-full bg-white/15 px-2.5 py-0.5 text-[10px] font-bold uppercase" data-testid="admin-role-badge">{admin.role}</span>
        <button onClick={onLogout} className="flex items-center gap-1 text-xs font-bold text-white/90 hover:text-white" data-testid="admin-logout-btn"><LogOut className="h-4 w-4" />Log out</button>
      </div>
    </div>
  </div>
);

const useOnline = () => {
  const [on, setOn] = useState(navigator.onLine);
  useEffect(() => {
    const a = () => setOn(true), b = () => setOn(false);
    window.addEventListener("online", a); window.addEventListener("offline", b);
    return () => { window.removeEventListener("online", a); window.removeEventListener("offline", b); };
  }, []);
  return on;
};

export const Topbar = ({ admin, cur, go, sections, openMenu }) => {
  const online = useOnline();
  const newItems = NEW.filter(([k]) => sections.some((s) => s.key === k));
  return (
    <header className="sticky top-0 z-30 border-b border-[#E3E6DD] bg-[#F7F5EF]/90 px-4 py-3 backdrop-blur-md lg:px-8" data-testid="admin-topbar">
      <div className="flex items-center gap-3 lg:gap-6">
        <button onClick={openMenu} className="grid h-10 w-10 place-items-center rounded-full bg-[#0D3B2B] text-white lg:hidden" data-testid="admin-menu-btn"><Menu className="h-5 w-5" /></button>
        <div className="min-w-0 lg:w-64"><h1 className="ks-serif truncate text-xl text-[#0D3B2B] lg:text-2xl" data-testid="admin-section-title">{cur.label}</h1>
          <p className="hidden truncate text-xs text-[#6B7F74] sm:block">{cur.sub}</p></div>
        <div className="hidden flex-1 md:block"><AdminSearch sections={sections} go={go} /></div>
        <div className="ml-auto flex items-center gap-2">
          {"Notification" in window && Notification.permission !== "granted" && (
            <button onClick={() => Notification.requestPermission()} title="Enable desktop alerts" data-testid="admin-enable-alerts-btn" className="grid h-10 w-10 place-items-center rounded-full border border-[#E3E6DD] bg-white text-[#0D3B2B]"><BellRing className="h-4 w-4" /></button>
          )}
          {!!newItems.length && (
            <DropdownMenu>
              <DropdownMenuTrigger className="inline-flex h-10 items-center gap-1.5 rounded-full bg-[#0D3B2B] px-4 text-sm font-bold text-white" data-testid="admin-new-btn"><Plus className="h-4 w-4" />New</DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="z-[1200]">{newItems.map(([k, l]) => <DropdownMenuItem key={k} onClick={() => go(k)} data-testid={`admin-new-${k}`}>{l}</DropdownMenuItem>)}</DropdownMenuContent>
            </DropdownMenu>
          )}
          <div className="hidden text-right xl:block">
            <p className="font-mono text-xs font-bold text-[#0D3B2B]">{new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</p>
            <p className={`text-[11px] font-bold ${online ? "text-[#2E9E6B]" : "text-[#E05C3A]"}`} data-testid="admin-online-status">{online ? "● Online" : "● Offline"}</p>
          </div>
        </div>
      </div>
      <div className="mt-3 md:hidden"><AdminSearch sections={sections} go={go} /></div>
    </header>
  );
};

export const MobileNav = ({ open, setOpen, ...p }) => (
  <Sheet open={open} onOpenChange={setOpen}>
    <SheetContent side="left" className="w-72 border-0 p-0" data-testid="admin-mobile-nav"><SheetTitle className="sr-only">Menu</SheetTitle>
      <SidebarBody {...p} go={(k) => { setOpen(false); p.go(k); }} /></SheetContent>
  </Sheet>
);
